import Avatar from '-/Custom/Avatar';
import type { ComponentProps, ReactNode } from 'react';
import type { ScrollView, ViewProps } from 'react-native';
import Animated, {
  type SharedValue,
  useAnimatedProps,
  useAnimatedRef,
  useAnimatedStyle,
  useDerivedValue,
  useScrollOffset,
} from 'react-native-reanimated';
import { StyleSheet } from 'react-native-unistyles';
import Box from '@/components/UI/Box';
import Text from '@/components/UI/Text';

export const PERSON_PROFILE_MASTHEAD_MIN_HEIGHT = 320;
const PERSON_PROFILE_MASTHEAD_COLLAPSE_DISTANCE = 210;
const PERSON_PROFILE_MASTHEAD_FADE_PROGRESS = 0.65;
const PERSON_PROFILE_MASTHEAD_FADE_DISTANCE =
  PERSON_PROFILE_MASTHEAD_COLLAPSE_DISTANCE *
  PERSON_PROFILE_MASTHEAD_FADE_PROGRESS;
const PERSON_PROFILE_AVATAR_MIN_SCALE = 0.72;
const PERSON_PROFILE_TEXT_TRANSLATE_Y = -20;
const PERSON_PROFILE_COMPACT_HEADER_THRESHOLD = 120;
const PERSON_PROFILE_COMPACT_HEADER_FADE_DISTANCE = 40;
const PERSON_PROFILE_COMPACT_HEADER_TRANSLATE_Y = 6;

type AnimatedVisibilityProps = Pick<
  ViewProps,
  'accessibilityElementsHidden' | 'aria-hidden' | 'importantForAccessibility'
>;

type PersonProfileMastheadProps = {
  actionContent?: ReactNode;
  nameAction?: ReactNode;
  children?: ReactNode;
  avatarId?: string | null;
  avatarSize?: ComponentProps<typeof Avatar>['size'];
  color?: string | null;
  name: string;
  minHeight?: number;
  profileLabel: string;
  scrollY: SharedValue<number>;
  surfaceStyle?: ViewProps['style'];
  testID: string;
};

export default function PersonProfileMasthead({
  actionContent = null,
  nameAction,
  children,
  avatarId,
  avatarSize = '3xl',
  color,
  name,
  minHeight = PERSON_PROFILE_MASTHEAD_MIN_HEIGHT,
  profileLabel,
  scrollY,
  surfaceStyle,
  testID,
}: PersonProfileMastheadProps) {
  const collapseProgress = useDerivedValue(
    () =>
      clampProgress(
        Math.max(scrollY.get(), 0) / PERSON_PROFILE_MASTHEAD_COLLAPSE_DISTANCE,
      ),
    [scrollY],
  );
  const mastheadHidden = useDerivedValue(
    () => scrollY.get() >= PERSON_PROFILE_MASTHEAD_FADE_DISTANCE,
    [scrollY],
  );
  const containerAnimatedStyle = useAnimatedStyle(() => {
    const progress = collapseProgress.get();

    return {
      pointerEvents: mastheadHidden.get() ? 'none' : 'box-none',
      opacity: clampProgress(
        1 - progress / PERSON_PROFILE_MASTHEAD_FADE_PROGRESS,
      ),
    };
  }, [collapseProgress, mastheadHidden]);
  const containerAnimatedProps = useAnimatedProps<AnimatedVisibilityProps>(
    () => getAnimatedVisibilityProps(mastheadHidden.get()),
    [mastheadHidden],
  );
  const avatarAnimatedStyle = useAnimatedStyle(() => {
    const progress = collapseProgress.get();

    return {
      transform: [
        {
          scale: 1 - (1 - PERSON_PROFILE_AVATAR_MIN_SCALE) * progress,
        },
      ],
    };
  }, [collapseProgress]);
  const textAnimatedStyle = useAnimatedStyle(() => {
    const progress = collapseProgress.get();

    return {
      transform: [
        {
          translateY: PERSON_PROFILE_TEXT_TRANSLATE_Y * progress,
        },
      ],
    };
  }, [collapseProgress]);

  return (
    <Animated.View
      animatedProps={containerAnimatedProps}
      style={[styles.container, containerAnimatedStyle]}
      testID={testID}
    >
      <Box
        align="center"
        direction="vertical"
        justify="flex-end"
        style={[styles.surface, { minHeight }, surfaceStyle]}
      >
        {children ?? (
          <>
            <Animated.View style={[styles.avatarFrame, avatarAnimatedStyle]}>
              <Avatar
                id={avatarId ?? ''}
                size={avatarSize}
                color={color ?? ''}
                name={name}
              />
            </Animated.View>
            <Animated.View style={[styles.textFrame, textAnimatedStyle]}>
              <Box
                direction="horizontal"
                align="center"
                justify="center"
                space="xs"
              >
                <Text bold center size="4xl" style={styles.name}>
                  {name}
                </Text>
                {nameAction}
              </Box>
              {profileLabel ? (
                <Text center size="lg" variant="typographySecondary">
                  {profileLabel}
                </Text>
              ) : null}
            </Animated.View>
          </>
        )}
        {actionContent ? (
          <Box
            direction="horizontal"
            justify="center"
            space="md"
            style={styles.actions}
            wrap
          >
            {actionContent}
          </Box>
        ) : null}
      </Box>
    </Animated.View>
  );
}

export function usePersonProfileHeaderAnimation() {
  const scrollViewRef = useAnimatedRef<ScrollView>();
  const scrollY = useScrollOffset(scrollViewRef);
  const compactHeaderProgress = useDerivedValue(
    () =>
      clampProgress(
        (scrollY.get() - PERSON_PROFILE_COMPACT_HEADER_THRESHOLD) /
          PERSON_PROFILE_COMPACT_HEADER_FADE_DISTANCE,
      ),
    [scrollY],
  );
  const compactHeaderVisible = useDerivedValue(
    () => compactHeaderProgress.get() > 0,
    [compactHeaderProgress],
  );
  const compactHeaderAnimatedStyle = useAnimatedStyle(() => {
    const progress = compactHeaderProgress.get();

    return {
      height: 44,
      maxWidth: '100%',
      flexShrink: 1,
      flexDirection: 'row',
      alignItems: 'center',
      opacity: progress,
      pointerEvents: compactHeaderVisible.get() ? 'auto' : 'none',
      transform: [
        {
          translateY:
            PERSON_PROFILE_COMPACT_HEADER_TRANSLATE_Y * (1 - progress),
        },
      ],
    };
  }, [compactHeaderProgress, compactHeaderVisible]);
  const compactHeaderAnimatedProps = useAnimatedProps<AnimatedVisibilityProps>(
    () => getAnimatedVisibilityProps(!compactHeaderVisible.get()),
    [compactHeaderVisible],
  );

  const expandedHeaderAnimatedStyle = useAnimatedStyle(() => ({
    opacity: 1 - compactHeaderProgress.get(),
    pointerEvents: compactHeaderVisible.get() ? 'none' : 'auto',
  }));
  const expandedHeaderAnimatedProps = useAnimatedProps<AnimatedVisibilityProps>(
    () => getAnimatedVisibilityProps(compactHeaderVisible.get()),
  );

  return {
    expandedHeaderAnimatedProps,
    expandedHeaderAnimatedStyle,
    compactHeaderAnimatedProps,
    compactHeaderAnimatedStyle,
    scrollViewRef,
    scrollY,
  };
}

function clampProgress(value: number) {
  'worklet';

  return Math.min(Math.max(value, 0), 1);
}

function getAnimatedVisibilityProps(hidden: boolean): AnimatedVisibilityProps {
  'worklet';

  return {
    accessibilityElementsHidden: hidden,
    'aria-hidden': hidden,
    importantForAccessibility: hidden ? 'no-hide-descendants' : 'auto',
  };
}

const styles = StyleSheet.create((theme) => ({
  avatarFrame: {
    alignSelf: 'center',
  },
  name: {
    flexShrink: 1,
  },
  actions: {
    maxWidth: 760,
  },
  container: {
    overflow: 'hidden',
    pointerEvents: 'box-none',
    zIndex: 1,
  },
  surface: {
    backgroundColor: theme.colors.background,
    borderBottomColor: theme.colors.borderMuted,
    borderBottomWidth: theme.size(0.5),
    gap: theme.space('lg'),
    paddingBottom: theme.space('2xl'),
    paddingHorizontal: theme.space('lg'),
    paddingTop: theme.space('xl'),
    pointerEvents: 'box-none',
  },
  textFrame: {
    alignItems: 'center',
    gap: theme.space('xs'),
    maxWidth: 760,
    width: '100%',
  },
}));
